import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import SendIcon from "@mui/icons-material/Send";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { getAuth } from "firebase/auth";
import {
  collection,
  doc,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { parseActiveProject } from "../lib/activeProjectParser";
import { MemoryDrawer, type MemorySectionId } from "./MemoryDrawer";
import { JarvisTheme } from "../../theme/jarvisTheme";

type ChatRole = "user" | "assistant";

type ChatMessage = {
  id: string;
  role: ChatRole;
  content: string;
  intent?: string;
  model?: string;
  streaming?: boolean;
};

type ChatHistoryTurn = {
  role: ChatRole;
  content: string;
};

type ChatRequest = {
  message: string;
  userId?: string;
  history?: ChatHistoryTurn[];
  timezone?: string;
};

type ChatResponse = {
  response: string;
  intent: string;
  model: string;
};

function nextId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** User/assistant pairs share createdAt in a batch; sortKey breaks ties on reload. */
function compareStoredChatDocs(
  a: { data: () => Record<string, unknown> },
  b: { data: () => Record<string, unknown> },
): number {
  const aData = a.data();
  const bData = b.data();
  const aMs =
    aData.createdAt &&
    typeof aData.createdAt === "object" &&
    "toMillis" in aData.createdAt &&
    typeof aData.createdAt.toMillis === "function"
      ? aData.createdAt.toMillis()
      : 0;
  const bMs =
    bData.createdAt &&
    typeof bData.createdAt === "object" &&
    "toMillis" in bData.createdAt &&
    typeof bData.createdAt.toMillis === "function"
      ? bData.createdAt.toMillis()
      : 0;
  if (aMs !== bMs) return aMs - bMs;
  const order = (data: Record<string, unknown>) =>
    typeof data.sortKey === "number"
      ? data.sortKey
      : data.role === "user"
        ? 0
        : 1;
  return order(aData) - order(bData);
}

function todayLocalYmd(): string {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const year = parts.find((p) => p.type === "year")?.value ?? "1970";
  const month = parts.find((p) => p.type === "month")?.value ?? "01";
  const day = parts.find((p) => p.type === "day")?.value ?? "01";
  return `${year}-${month}-${day}`;
}

export function ChatTab() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeProject, setActiveProject] = useState("");
  const [hasEntryToday, setHasEntryToday] = useState<boolean | null>(null);
  const [memoryDrawerOpen, setMemoryDrawerOpen] = useState(false);
  const [memoryScrollTo, setMemoryScrollTo] = useState<MemorySectionId | null>(
    null,
  );
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const openMemoryDrawer = useCallback((section?: MemorySectionId) => {
    setMemoryScrollTo(section ?? null);
    setMemoryDrawerOpen(true);
  }, []);

  const uid = getAuth().currentUser?.uid ?? null;
  const projectConfig = useMemo(
    () => parseActiveProject(activeProject),
    [activeProject],
  );
  const focusLabel =
    projectConfig?.projectTitle ??
    activeProject.split("\n")[0]?.trim() ??
    activeProject;

  useEffect(() => {
    if (!uid) {
      setHistoryLoaded(true);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const q = query(
          collection(getFirestore(), "jarvis_chats", uid, "messages"),
          orderBy("createdAt", "desc"),
          limit(20),
        );
        const snap = await getDocs(q);
        if (cancelled) return;

        const loaded: ChatMessage[] = [...snap.docs]
          .sort(compareStoredChatDocs)
          .map((d) => {
            const data = d.data();
            const role =
              data.role === "user" || data.role === "assistant"
                ? data.role
                : "assistant";
            return {
              id: nextId(),
              role,
              content: typeof data.content === "string" ? data.content : "",
              intent:
                typeof data.intent === "string" ? data.intent : undefined,
              model: typeof data.model === "string" ? data.model : undefined,
              streaming: false,
            };
          });

        setMessages(loaded);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(
            e instanceof Error
              ? e.message
              : "Failed to load chat history.",
          );
        }
      } finally {
        if (!cancelled) {
          setHistoryLoaded(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [uid]);

  useEffect(() => {
    if (!uid) {
      setActiveProject("");
      return;
    }

    const ref = doc(
      getFirestore(),
      "jarvis_memory",
      uid,
      "active_project",
      "data",
    );

    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data();
        setActiveProject(
          typeof data?.content === "string" ? data.content.trim() : "",
        );
      },
      () => {
        setActiveProject("");
      },
    );

    return () => unsub();
  }, [uid]);

  useEffect(() => {
    if (!uid || !projectConfig?.entryType) {
      setHasEntryToday(null);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const q = query(
          collection(getFirestore(), "jarvis_entries", uid, "entries"),
          where("type", "==", projectConfig.entryType),
          where("date", "==", todayLocalYmd()),
          limit(1),
        );
        const snap = await getDocs(q);
        if (!cancelled) {
          setHasEntryToday(!snap.empty);
        }
      } catch {
        if (!cancelled) {
          setHasEntryToday(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [uid, projectConfig?.entryType, activeProject]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, loading]);

  const sendMessage = useCallback(async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading || !historyLoaded) return;

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      setError("Sign in required.");
      return;
    }

    const historySlice: ChatHistoryTurn[] = messages
      .filter((m) => !m.streaming && m.content.trim().length > 0)
      .slice(-16)
      .map((m) => ({ role: m.role, content: m.content }));

    const userMsg: ChatMessage = {
      id: nextId(),
      role: "user",
      content: text,
    };
    const assistantId = nextId();
    const assistantPlaceholder: ChatMessage = {
      id: assistantId,
      role: "assistant",
      content: "",
      streaming: true,
    };

    setMessages((prev) => [...prev, userMsg, assistantPlaceholder]);
    setInput("");
    setLoading(true);
    setError(null);

    try {
      const fn = httpsCallable<ChatRequest, ChatResponse, string>(
        getFunctions(),
        "chat",
      );

      const { stream, data } = await fn.stream({
        message: text,
        userId: user.uid,
        history: historySlice,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      for await (const chunk of stream) {
        if (typeof chunk !== "string" || !chunk) continue;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, content: m.content + chunk, streaming: true }
              : m,
          ),
        );
      }

      const result = await data;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId
            ? {
                ...m,
                content: result.response || m.content,
                intent: result.intent,
                model: result.model,
                streaming: false,
              }
            : m,
        ),
      );
    } catch (e: unknown) {
      setMessages((prev) => prev.filter((m) => m.id !== assistantId));
      setError(
        e instanceof Error
          ? e.message
          : "Chat failed. Deploy the chat function and set GEMINI_API_KEY.",
      );
    } finally {
      setLoading(false);
    }
  }, [input, loading, historyLoaded, messages]);

  const sendDisabled = loading || !input.trim() || !historyLoaded;

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: JarvisTheme.fonts.ui,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              fontSize: "0.8rem",
              color: JarvisTheme.colors.muted,
              mb: 0.5,
              opacity: 0.85,
            }}
          >
            Chat
          </Typography>
          <Typography
            sx={{
              opacity: 0.72,
              fontSize: "0.85rem",
              color: JarvisTheme.colors.muted,
            }}
          >
            Ask about tasks, focus, or general topics. Complex requests use
            advanced reasoning.
          </Typography>
        </Box>
        <Tooltip title="Memory">
          <IconButton
            onClick={() => openMemoryDrawer()}
            aria-label="Open memory drawer"
            sx={{
              color: JarvisTheme.colors.accent,
              border: `1px solid ${JarvisTheme.colors.accentBorder}`,
              bgcolor: JarvisTheme.colors.accentSoft,
              "&:hover": {
                bgcolor: "rgba(88, 230, 255, 0.22)",
              },
            }}
          >
            <PsychologyOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {activeProject && (
        <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Chip
            label={`Focus: ${focusLabel}`}
            size="small"
            onClick={() => openMemoryDrawer("active_project")}
            sx={{
              height: 24,
              maxWidth: "100%",
              fontSize: "0.7rem",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              fontFamily: JarvisTheme.fonts.mono,
              bgcolor: JarvisTheme.colors.accentSoft,
              color: JarvisTheme.colors.accent,
              border: `1px solid ${JarvisTheme.colors.accentBorder}`,
              cursor: "pointer",
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
              "&:hover": {
                bgcolor: "rgba(88, 230, 255, 0.22)",
              },
            }}
          />
          {projectConfig?.entryType && hasEntryToday !== null && (
            <Chip
              label={hasEntryToday ? "1+ entries today" : "No entries today"}
              size="small"
              sx={{
                height: 24,
                fontSize: "0.7rem",
                letterSpacing: "0.08em",
                fontFamily: JarvisTheme.fonts.mono,
                bgcolor: JarvisTheme.colors.panelSoft,
                color: JarvisTheme.colors.muted,
                border: `1px solid ${JarvisTheme.colors.panelBorder}`,
              }}
            />
          )}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        ref={scrollRef}
        sx={{
          height: { xs: "50vh", sm: "60vh" },
          overflowY: "auto",
          mb: 2,
          pr: 0.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {!historyLoaded && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress
              size={22}
              sx={{ color: JarvisTheme.colors.accent }}
            />
          </Box>
        )}

        {historyLoaded && messages.length === 0 && !loading && (
          <Typography
            sx={{
              color: JarvisTheme.colors.muted,
              fontSize: "0.85rem",
              textAlign: "center",
              py: 4,
              opacity: 0.75,
            }}
          >
            Send a message to start.
          </Typography>
        )}

        {messages.map((msg) => {
          const isUser = msg.role === "user";
          const isStub = msg.model === "stub";
          const isPro = msg.model === "gemini-2.5-pro";
          const showSpinner =
            msg.streaming && !msg.content && loading && !isUser;

          return (
            <Box
              key={msg.id}
              sx={{
                display: "flex",
                justifyContent: isUser ? "flex-end" : "flex-start",
              }}
            >
              <Box
                sx={{
                  maxWidth: "85%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: isUser ? "flex-end" : "flex-start",
                  gap: 0.5,
                }}
              >
                {!isUser && isStub && (
                  <Chip
                    label="Coming Soon"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      letterSpacing: "0.12em",
                      fontFamily: JarvisTheme.fonts.mono,
                      bgcolor: JarvisTheme.colors.panelSoft,
                      color: JarvisTheme.colors.muted,
                      border: `1px solid ${JarvisTheme.colors.panelBorder}`,
                    }}
                  />
                )}
                {!isUser && isPro && (
                  <Chip
                    label="Pro"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      letterSpacing: "0.12em",
                      fontFamily: JarvisTheme.fonts.mono,
                      bgcolor: JarvisTheme.colors.accentSoft,
                      color: JarvisTheme.colors.accent,
                      border: `1px solid ${JarvisTheme.colors.accentBorder}`,
                    }}
                  />
                )}
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 1,
                    border: `1px solid ${
                      isUser
                        ? JarvisTheme.colors.accentBorder
                        : JarvisTheme.colors.panelBorder
                    }`,
                    bgcolor: isUser
                      ? JarvisTheme.colors.accentSoft
                      : JarvisTheme.colors.panelSoft,
                    opacity: isStub ? 0.72 : 1,
                  }}
                >
                  {showSpinner ? (
                    <CircularProgress
                      size={18}
                      sx={{ color: JarvisTheme.colors.accent }}
                    />
                  ) : (
                    <Typography
                      sx={{
                        fontFamily: JarvisTheme.fonts.ui,
                        fontSize: "0.9rem",
                        color: JarvisTheme.colors.text,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {msg.content ||
                        (msg.streaming ? "" : "…")}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <TextField
          multiline
          maxRows={4}
          fullWidth
          placeholder="Message Jarvis…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void sendMessage();
            }
          }}
          disabled={loading || !historyLoaded}
          sx={{
            flex: 1,
            minWidth: 200,
            "& .MuiOutlinedInput-root": {
              fontFamily: JarvisTheme.fonts.ui,
              color: JarvisTheme.colors.text,
              bgcolor: JarvisTheme.colors.panelSoft,
              "& fieldset": { borderColor: JarvisTheme.colors.panelBorder },
              "&:hover fieldset": {
                borderColor: JarvisTheme.colors.accentBorder,
              },
              "&.Mui-focused fieldset": {
                borderColor: JarvisTheme.colors.accent,
              },
            },
            "& .MuiInputBase-input::placeholder": {
              color: JarvisTheme.colors.muted,
              opacity: 0.7,
            },
          }}
        />
        <Button
          variant="contained"
          onClick={() => void sendMessage(undefined)}
          disabled={sendDisabled}
          endIcon={<SendIcon />}
          sx={{
            fontFamily: JarvisTheme.fonts.ui,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            bgcolor: JarvisTheme.colors.accent,
            color: JarvisTheme.colors.bg0,
            "&:hover": { bgcolor: "rgba(88, 230, 255, 0.85)" },
            "&.Mui-disabled": {
              bgcolor: JarvisTheme.colors.accentTrack,
              color: JarvisTheme.colors.muted,
            },
          }}
        >
          Send
        </Button>
      </Box>

      <MemoryDrawer
        open={memoryDrawerOpen}
        onClose={() => {
          setMemoryDrawerOpen(false);
          setMemoryScrollTo(null);
        }}
        scrollToSection={memoryScrollTo}
      />
    </Box>
  );
}
